import { useState } from "react";
import s from "./Weather.module.css";
import { Page } from "../../layout/AppShell";
import { useCanWork } from "../../api/queries";
import { KIND_LABEL, negotiable, WORK_LABEL, workVerdict, type CanWork } from "../../domain/weather";
import { Card, EmptyState, ErrorState, Field, Input, Skeleton } from "../../design/components";
import { locale, t } from "../../i18n";

/* a cleared or half-typed date is not a date: answer with nothing rather than crash the tab */
const isoOrNull = (v: string): string | null => {
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};
const isoLocal = (d: Date) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);

/**
 * Whether the weather allows the work — and whether we actually know.
 *
 * The page is built around a tri-state, and the third value is the one that
 * matters. `allowed: null` means the limits could not be evaluated: absence of
 * a breach is not permission, and a screen that renders "no breaches" as a
 * green light is the failure the module exists to prevent.
 *
 * It also keeps the KIND of each limit visible, because they are not
 * negotiable in the same way. A material limit ruins the work and can be
 * argued about; a personnel limit hurts somebody; a regulatory ban is a clock
 * rule that a cool day does not lift, however willing the crew is.
 */
export function WeatherPage() {
  const [category, setCategory] = useState("crack-seal");
  const [at, setAt] = useState(isoLocal(new Date()));
  const iso = isoOrNull(at);
  const answer = useCanWork(iso ? category.trim() || null : null, iso ?? "");

  return (
    <Page eyebrow="Deep thinking" title="Can the work happen">
      <Card title="The question" sub="A limit that cannot be evaluated is reported as unknown, never as a pass.">
        <div className={s.form}>
          <Field label="Work category">
            <Input
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder={t("e.g. crack-seal")}
              aria-label={t("Work category")}
            />
          </Field>
          <Field label="At">
            <Input type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} aria-label={t("At")} />
          </Field>
        </div>
      </Card>

      {answer.isPending && <Skeleton rows={5} />}
      {answer.isError && <ErrorState error={answer.error} retry={() => answer.refetch()} />}
      {!category.trim() && <EmptyState>Name the work and the engine will say whether it can happen.</EmptyState>}
      {answer.data && <Verdict answer={answer.data} />}
    </Page>
  );
}

function Verdict({ answer }: { answer: CanWork }) {
  const v = workVerdict(answer);
  const c = answer.conditions;
  return (
    <>
      <div
        data-testid="weather-verdict"
        className={`${s.verdict} ${v === "allowed" ? s.allowed : v === "blocked" ? s.blocked : s.unknown}`}
      >
        <div className={s.eyebrow}>{t(WORK_LABEL[v])}</div>
        <div className={s.headline}>
          {v === "allowed"
            ? t("The work can go ahead")
            : v === "blocked"
              ? t("The work cannot go ahead")
              : t("Not established — this is not permission")}
        </div>
        <div className={s.reason}>{answer.verdict}</div>
        <div className={s.basis}>
          {answer.limitsOnFile === 1
            ? t("{made} of {n} limit on file could be evaluated.", { made: answer.checksMade, n: answer.limitsOnFile })
            : t("{made} of {n} limits on file could be evaluated.", {
                made: answer.checksMade,
                n: answer.limitsOnFile,
              })}
          {/* observation or forecast, never merged into one another */}
          {c.known && c.basis ? t(" Conditions from {basis}.", { basis: c.basis }) : ""}
          {!c.known && c.whyNot ? ` ${c.whyNot}` : ""}
        </div>
      </div>

      {c.known && (
        <Card title="What was measured" sub="The readings the verdict was built from.">
          <div className={s.readings}>
            {c.airTempC != null && (
              <div className={s.reading}>
                <span>{t("Air")}</span>
                <b>{c.airTempC} °C</b>
              </div>
            )}
            {c.surfaceTempC != null && (
              <div className={s.reading}>
                <span>{t("Surface")}</span>
                <b>{c.surfaceTempC} °C</b>
              </div>
            )}
            {c.windKt != null && (
              <div className={s.reading}>
                <span>{t("Wind")}</span>
                <b>{c.windKt} kt</b>
              </div>
            )}
            {c.gustKt != null && (
              <div className={s.reading}>
                <span>{t("Gust")}</span>
                <b>{c.gustKt} kt</b>
              </div>
            )}
            {c.visibilityM != null && (
              <div className={s.reading}>
                <span>{t("Visibility")}</span>
                <b>{c.visibilityM.toLocaleString(locale())} m</b>
              </div>
            )}
          </div>
        </Card>
      )}

      {answer.breaches.length > 0 && (
        <Card title="Breached" sub="Not all of these can be waited out — the kind says which.">
          <div className={s.limits}>
            {answer.breaches.map((b, i) => (
              <div key={i} className={`${s.limit} ${s[b.kind] ?? ""}`}>
                <div className={s.limitKind}>
                  {KIND_LABEL[b.kind] ? t(KIND_LABEL[b.kind]) : b.kind}
                  {!negotiable(b.kind) ? t(" · waiting will not help") : ""}
                </div>
                <div className={s.reason}>{b.why}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {answer.unknowns.length > 0 && (
        <Card
          title="Could not be evaluated"
          sub="These are readings nobody has taken. They are not passes, and the work is not cleared by their silence."
        >
          <div className={s.limits}>
            {answer.unknowns.map((u, i) => (
              <div key={i} className={`${s.limit} ${s[u.kind] ?? ""}`}>
                <div className={s.limitKind}>{KIND_LABEL[u.kind] ? t(KIND_LABEL[u.kind]) : u.kind}</div>
                <div className={s.reason}>{u.why}</div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </>
  );
}
